import React, { memo } from 'react';
import { useLocation } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';
import { ChappiChatbot } from '../common/chappi/chappi-chatbot';
import { EnvironmentService } from '../common/environment/environment-service';
import { DashboardRouteProps, dashboardRoutes } from '../common/routes';
import { useListUserViewDashboards } from '../common/v2/api/dashboards/hooks';
import { BetterLoadingOverlay } from '../common/v2/common/components/BetterLoading';
import { Dashboard } from '../common/v2/common/components/dashboards/types';
import RestrictedDashboardErrorComponent from '../common/v2/common/components/errors/pages/RestrictedDashboardError';
import { DEFAULT_LANDING_PAGE } from '../common/v2/constants';
import {
  useEnvironmentServiceContext,
  useFeatureFlagsServiceContext,
  useLatestPreferencesContext,
} from '../common/v2/context/contexts';
import { DashboardContextProvider } from '../common/v2/context/DashboardContextProvider';
import { LegacyDomainScopedContextProvider } from '../common/v2/context/LegacyDomainScopedContextProvider';
import { PrivateRoute } from './PrivateRoute';

// this override is just for dev/debug purposes
const allowAllDashboardRoutes = false;

const getPermittedDashboardRoutes = (
  allowedDashboards: Dashboard[],
  environmentService: EnvironmentService
) => {
  const permittedDashboards = allowedDashboards.filter((db) => db.enabled);
  let permittedDashboardRoutes: DashboardRouteProps[];
  if (environmentService.isLocalhost && allowAllDashboardRoutes) {
    permittedDashboardRoutes = dashboardRoutes;
  } else {
    permittedDashboardRoutes = dashboardRoutes.filter(
      (route) =>
        permittedDashboards &&
        permittedDashboards.some(({ link }) => {
          return (
            route.path && route.path.includes(link.replace('/dashboard/', ''))
          );
        })
    );
  }
  return permittedDashboardRoutes;
};

const DashboardRoutesInner = memo(() => {
  const latestPreferences = useLatestPreferencesContext();
  const companyLandingPage =
    latestPreferences.customLandingPage ?? DEFAULT_LANDING_PAGE;
  const environmentService = useEnvironmentServiceContext();
  const { dashboards: allowedDashboards, isLoading } =
    useListUserViewDashboards();
  const location = useLocation();

  if (isLoading) {
    return (
      <BetterLoadingOverlay
        debugString="DashboardRoutes"
        isPending={isLoading}
      />
    );
  }

  const permittedDashboardRoutes = getPermittedDashboardRoutes(
    allowedDashboards,
    environmentService
  );
  // Check if current path is a dashboard that user doesn't have access to
  const currentPath = location.pathname;
  const isDashboardPermitted = permittedDashboardRoutes.some((route) => {
    const path = route.path as string;
    const dashboardPath = path.split('/')[2];
    const currentDashboardPath = currentPath.split('/')[2];
    return dashboardPath === currentDashboardPath;
  });
  if (!isDashboardPermitted) {
    const permittedPaths = permittedDashboardRoutes.map((r) => r.path);
    console.error(
      `User attempted to access restricted dashboard: ${currentPath}. ` +
        `Permitted dashboards: ${permittedPaths.join(', ')}`
    );

    return <RestrictedDashboardErrorComponent />;
  }
  return (
    <Switch>
      {permittedDashboardRoutes.map((route) => {
        return (
          <PrivateRoute key={route.id} component={route.comp()} {...route} />
        );
      })}
      <Redirect from="*" to={companyLandingPage} />
    </Switch>
  );
});
DashboardRoutesInner.displayName = 'DashboardRoutesInner';

export const DashboardRoutes = () => {
  const featureFlagsService = useFeatureFlagsServiceContext();
  return (
    <DashboardContextProvider>
      <LegacyDomainScopedContextProvider>
        <DashboardRoutesInner />
      </LegacyDomainScopedContextProvider>
      {featureFlagsService.isChappiEnabled() && <ChappiChatbot />}
    </DashboardContextProvider>
  );
};
